import { createContext, useContext, useEffect, useState } from 'react';

import { LoaderSmall } from '../LoaderSmall';
import { useProspect } from '../ProspectProvider';

import type { EnvType } from '../../types/env';

interface ABContextValue {
  experiments: Record<string, unknown>;
  relation_id: string;
}

const context = createContext<ABContextValue | undefined>(undefined);

type Props = {
  children: React.ReactNode;
  deviceId: string;
  env: EnvType;
  application: 'NL_DI' | 'NL_PNC' | 'FR_PNC';
};

export const ABProvider: React.FC<Props> = ({ children, deviceId, env, application }) => {
  const prospectId = useProspect();
  const [data, setData] = useState<{ relation_id: string; experiments: Record<string, unknown> }>();
  useEffect(() => {
    const prefix = env === 'prod' ? '' : env === 'staging' ? 'staging.' : 'dev.';
    fetch(`https://${prefix}ab.insify.com/ab-experiments/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        prospect_id: prospectId,
        device_id: deviceId,
        application_id: application,
      }),
    })
      .then(
        (response) =>
          response.json() as Promise<{
            hashes: Record<string, string>;
            experiments: { key: string; experiments: Record<string, unknown> };
          }>,
      )
      .then((response) => {
        const { experiments, hashes } = response;
        setData({
          experiments: experiments,
          relation_id: hashes.relation_id,
        });
      })
      .catch(() => setData({ relation_id: '', experiments: {} }));
  }, [prospectId, deviceId]);

  if (!data) {
    return <LoaderSmall />;
  }

  return <context.Provider value={data}>{children}</context.Provider>;
};

export const useAB = () => {
  const ctx = useContext(context);

  if (ctx === undefined) {
    throw new Error(`useAB must be used within a ABProvider`);
  }

  return ctx;
};
